import {
  Box,
  FormControl,
  Menu,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import pluralize from "pluralize";
import { useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import useColors from "../../api/hooks/useColors";
import SvgToIcon from "../../icons/SvgToIcon";
import { icons } from "../../scenes/stack/environment/icons/iconExport";
import ContainedButton from "../buttons/ContainedButton";
import TextButton from "../buttons/TextButton";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import { StyledTextField } from "../forms/styles/formFieldStyles";

const ExclusionMenu = ({
  anchorEl,
  handleClose,
  handleExclusion,
  isExcludingAll,
  isExcludingMisconfiguration,
}) => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const exclusionReasonMaxLength = 100;
  const customReasonOption = "Custom...";
  const exclusionReasons = [
    "Incorrect Data",
    "Risk Accepted",
    "Not Relevant",
    customReasonOption,
  ];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoadingExclusion, setIsLoadingExclusion] = useState(false);

  const excludedObject = isExcludingMisconfiguration
    ? "misconfiguration"
    : "entity";

  const handleMenuItemClick = () => {
    setIsDialogOpen(true);
  };

  const onMenuClose = () => {
    trackEvent("User closed Exclusion menu");
    handleClose();
    setSelectedReason("");
    setCustomReason("");
  };

  const onDialogClose = () => {
    trackEvent("User closed Exclusion confirmation dialog");
    setIsDialogOpen(false);
  };

  const onDialogConfirm = async () => {
    const finalReason =
      selectedReason === customReasonOption ? customReason : selectedReason;
    trackEvent(
      "User pressed on 'Exclude from Risk' button in Exclusion confirmation dialog with reason: " +
        finalReason
    );
    setIsLoadingExclusion(true);

    try {
      await handleExclusion(finalReason);
      setIsDialogOpen(false);
      onMenuClose();
    } finally {
      setIsLoadingExclusion(false);
    }
  };

  const SelectChevronIcon = () => (
    <SvgToIcon
      IconLogo={icons.chevronDownIcon}
      color={colors.icon_action_active}
      width="20px"
    />
  );

  const dialogContentFirstLine = () => {
    if (isExcludingAll) {
      const excludedObjects = isExcludingMisconfiguration
        ? "misconfigurations"
        : "affected entities";
      const excludedFrom = isExcludingMisconfiguration
        ? "control"
        : "misconfiguration";

      return (
        <>
          <Typography variant="body1">
            You are about to exclude all {excludedObjects} of this
            {" " + excludedFrom} from this specific risk assessment.
          </Typography>
          <Typography variant="body1">
            It may reappear if future scans detect new entities — but previously
            excluded {excludedObjects} will remain excluded.
          </Typography>
        </>
      );
    } else {
      return (
        <>
          <Typography variant="body1">
            You are about to exclude this {excludedObject} from this specific
            risk assessment.
          </Typography>
          <Typography variant="body1">
            It will not appear again unless you manually include it back.
          </Typography>
        </>
      );
    }
  };

  const dialogContentSecondLine = () => {
    if (isExcludingAll) {
      const excludedFrom = isExcludingMisconfiguration
        ? "control"
        : "misconfiguration";

      return `By continuing, you accept the risk of no longer tracking these ${pluralize(
        excludedObject
      )} for this ${excludedFrom}.`;
    } else {
      return `By continuing, you accept the risk of no longer tracking this ${excludedObject} in this risk assessment.`;
    }
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onMenuClose}
        slotProps={{
          paper: {
            sx: {
              width: "240px",
              borderRadius: "10px",
              boxShadow: `0px 2px 4px 0px ${colors.cardEntityShadow}`,
              padding: "8px 16px",
              backgroundColor: colors.modal_bg,
            },
          },
        }}
      >
        <Typography
          variant="overline"
          sx={{
            marginBottom: "10px",
            color: colors.text_primary,
          }}
        >
          Why are you excluding?
        </Typography>
        <FormControl fullWidth sx={{ marginBottom: "10px" }}>
          <Select
            value={selectedReason}
            displayEmpty
            onChange={(e) => setSelectedReason(e.target.value)}
            IconComponent={SelectChevronIcon}
            renderValue={(selected) => {
              if (selected === "") {
                return (
                  <Typography
                    variant="caption"
                    sx={{ color: colors.icon_action_active }}
                  >
                    Select a reason...
                  </Typography>
                );
              }
              return (
                <Typography
                  variant="caption"
                  sx={{ color: colors.text_primary }}
                >
                  {selected}
                </Typography>
              );
            }}
            sx={{
              borderRadius: "10px",
              border: `1px solid ${colors.grey_20percent}`,
              height: "32px",
              marginTop: "8px",
              padding: "0px",
              paddingRight: "6px",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-root": {
                boxShadow: "none",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: "10px",
                },
              },
            }}
          >
            {exclusionReasons.map((reason) => (
              <MenuItem
                key={reason}
                value={reason}
                sx={{
                  borderRadius: "10px",
                  borderColor: colors.grey_20percent,
                  margin: "8px",
                  padding: "8px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: colors.text_primary }}
                >
                  {reason}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedReason === customReasonOption && (
          <>
            <StyledTextField
              label={
                <Typography variant="caption">Enter your reason</Typography>
              }
              InputLabelProps={{
                shrink: true,
                sx: { color: colors.text_primary },
              }}
              fullWidth
              multiline
              autoFocus
              rows={4}
              variant="outlined"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              inputProps={{ maxLength: exclusionReasonMaxLength }}
              sx={{
                borderRadius: "10px",
                width: "100%",
                marginTop: "8px",
                "& .MuiOutlinedInput-root": {
                  padding: "16px  14px",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: `1px solid ${colors.grey_20percent}`,
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: `1px solid ${colors.grey_20percent}`,
                  },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                  color: colors.text_primary,
                  lineHeight: 1.5,
                  overflowWrap: "break-word",
                  whiteSpace: "pre-wrap",
                },
              }}
              placeholder="Reason for excluding is...."
              helperText={
                <Typography
                  variant="caption"
                  sx={{ color: colors.helper_text }}
                >
                  {customReason.length}/{exclusionReasonMaxLength}
                </Typography>
              }
            />
          </>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginTop: "12px" }}
        >
          <TextButton onClick={onMenuClose}>
            <Typography variant="overline">Cancel</Typography>
          </TextButton>
          <ContainedButton
            color="primary"
            size="small"
            disabled={
              !selectedReason ||
              (selectedReason === customReasonOption && !customReason.trim())
            }
            onClick={handleMenuItemClick}
          >
            <Typography variant="overline">Exclude</Typography>
          </ContainedButton>
        </Box>
      </Menu>

      <ConfirmationDialog
        openDialog={isDialogOpen}
        handleDialogClose={onDialogClose}
        handleConfirm={onDialogConfirm}
        isLoading={isLoadingExclusion}
        isLoadingText={"Excluding..."}
        dialogContentFirstLine={dialogContentFirstLine()}
        dialogContentSecondLine={dialogContentSecondLine()}
        confirmationButtonText={"Exclude from Risk"}
        cancelButtonText={"Cancel"}
        sx={{
          "& .MuiButton-text": { color: colors.error_400 },
        }}
        showCloseButton={true}
      />
    </>
  );
};
export default ExclusionMenu;
